import React, { useMemo } from 'react';
import zxcvbn from 'zxcvbn';
import { useLanguage } from '../../i18n/useLanguage';

interface PasswordStrengthMeterProps {
  password: string;
}

const RULES = [
  { key: 'password.ruleMinLength', test: (pw: string) => pw.length >= 8 },
  { key: 'password.needsUppercase', test: (pw: string) => /[A-Z]/.test(pw) },
  { key: 'password.needsLowercase', test: (pw: string) => /[a-z]/.test(pw) },
  { key: 'password.needsDigit', test: (pw: string) => /[0-9]/.test(pw) },
  { key: 'password.needsSpecial', test: (pw: string) => /[^A-Za-z0-9]/.test(pw) },
] as const;

const STRENGTH_COLORS = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e'];

export const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({ password }) => {
  const { t } = useLanguage();

  const result = useMemo(() => (password ? zxcvbn(password) : null), [password]);
  const ruleResults = useMemo(
    () => RULES.map((r) => ({ label: t(r.key as any), passed: password ? r.test(password) : false })),
    [password, t],
  );
  const allRulesPassed = ruleResults.every((r) => r.passed);

  if (!result) return null;

  const effectiveScore = allRulesPassed ? result.score : Math.min(result.score, 1);
  const labels = [
    t('password.veryWeak'),
    t('password.weak'),
    t('password.fair'),
    t('password.strong'),
    t('password.veryStrong'),
  ];

  return (
    <div className="password-strength">
      <div className="password-strength-bar-track">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="password-strength-bar-segment"
            style={{
              backgroundColor: i <= effectiveScore ? STRENGTH_COLORS[effectiveScore] : 'var(--color-border)',
            }}
          />
        ))}
      </div>
      <span className="password-strength-label" style={{ color: STRENGTH_COLORS[effectiveScore] }}>
        {labels[effectiveScore]}
      </span>

      <ul className="password-rules-list">
        {ruleResults.map((r, i) => (
          <li key={i} className={`password-rule ${r.passed ? 'password-rule--passed' : 'password-rule--failed'}`}>
            <span className="password-rule-icon">{r.passed ? '✓' : '✗'}</span>
            {r.label}
          </li>
        ))}
      </ul>
    </div>
  );
};
